import React, { useState, useEffect } from "react";
import learningStyle from "../MyLearnings/MyLearning.module.css";
import dashboardStyle from "./GoalsPopup.module.css";
import GoalDetail from "../GoalDetails/GoalDetails";
import Loader from "../Loder/Loader";
import { baseurl } from "../Url";
import { Snackbar, Alert } from "@mui/material";
import { logActivity } from "../../services/activityService";

const GoalsPopup = ({ open, onClose, onSuccess, theme = "default" }) => {
  const style = theme === "dashboard" ? dashboardStyle : learningStyle;
  const key = JSON.parse(localStorage.getItem("access-token"));
  const UserID = localStorage.getItem("USERID");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [learningId, setLearningId] = useState("");
  const [hasGoals, setHasGoals] = useState(false);
  const [careerGoals, setCareerGoals] = useState(null);
  const [noLearnings, setNoLearnings] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isConfirmationVisible, setConfirmationVisible] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isUpdatingGoals, setIsUpdatingGoals] = useState(false);
  const [skillFocusOptions, setSkillFocusOptions] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);

  const totalSteps = 4;

  const [formData, setFormData] = useState({
    user_id: UserID || "",
    learning_id: "",
    primary_career_intention: [],
    target_role: "",
    target_level: "",
    skill_focus: [],
    skill_type: "",
    timeline: "",
    weekly_commitment: "",
  });

  const getNormalizedList = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean).map((v) => String(v).trim()).filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
    return [];
  };

  const buildInitialFormData = (goalData = {}) => ({
    user_id: UserID || "",
    learning_id: "",
    primary_career_intention: getNormalizedList(goalData.primary_career_intention),
    target_role: goalData.target_role || "",
    target_level: goalData.target_level || "",
    skill_focus: getNormalizedList(goalData.skills ?? goalData.skill_focus),
    skill_type: goalData.skill_type || "",
    timeline: goalData.timeline || "",
    weekly_commitment: goalData.weekly_commitment || "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "target_role" || field === "target_level") {
        next.skill_focus = [];
        setSkillFocusOptions([]);
      }
      return next;
    });
  };

  const handleCheckboxChange = (field, option, checked) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      const updated = checked
        ? [...current, option]
        : current.filter((v) => v !== option);
      return { ...prev, [field]: updated };
    });
  };

  useEffect(() => {
    const targetRole = formData.target_role;
    const targetLevel = formData.target_level;
    if (!targetRole) {
      setSkillFocusOptions([]);
      return;
    }
    const fetchSkills = async () => {
      setSkillsLoading(true);
      try {
        const response = await fetch(`${baseurl}/goal/skills/by-role`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            target_role: targetRole,
            target_level: targetLevel || undefined,
          }),
        });
        const data = await response.json();
        if (data.status === true && Array.isArray(data.data)) {
          setSkillFocusOptions(data.data);
        } else {
          setSkillFocusOptions([]);
        }
      } catch (err) {
        console.error("Error fetching skills by role:", err);
        setSkillFocusOptions([]);
      } finally {
        setSkillsLoading(false);
      }
    };
    fetchSkills();
  }, [formData.target_role, formData.target_level]);

  const formDataToGoalSetupPayload = () => {
    const payload = {
      user_id: localStorage.getItem("USERID") || "",
      target_role: formData.target_role || "",
      target_level: formData.target_level || "",
      skills: formData.skill_focus || [],
      primary_career_intention: Array.isArray(formData.primary_career_intention)
        ? formData.primary_career_intention.join(", ")
        : formData.primary_career_intention || "",
      skill_type: formData.skill_type || "",
      timeline: formData.timeline || "",
      weekly_commitment: formData.weekly_commitment || "",
    };
    if (careerGoals?.goal_id) {
      payload.goal_id = careerGoals.goal_id;
    }
    return payload;
  };

  const callUserGoalsAPI = async () => {
    const payload = formDataToGoalSetupPayload();

    if (!payload.target_role) {
      setSnackbarMessage("Target role is required");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${baseurl}/goal/setup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === true) {
        logActivity({ module: "GOALS", action: "GOALS_SAVED", remark: "Career goals saved successfully" });
        setSnackbarMessage(data.message || "Career goal setup created successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setCurrentStep(1);
        setIsUpdatingGoals(false);
        onSuccess?.();
        onClose?.();
      } else {
        setSnackbarMessage(data.message || data.error || "Error creating goal setup");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("An error occurred while adding Goals");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteUserGoals = async () => {
    const userId = localStorage.getItem("USERID");
    const LearningIdLoc = localStorage.getItem("newLearningId");

    if (!userId || !LearningIdLoc) {
      setSnackbarMessage("User ID or Learning ID is missing.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await fetch(`${baseurl}/api/usergoals-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          learning_id: LearningIdLoc,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbarMessage("Goals reset successfully! You can set new goals now.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setConfirmationVisible(false);
        setHasGoals(false);
        onSuccess?.();
      } else {
        setSnackbarMessage(data.message || "Error resetting goals");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("Error during reset");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const closeGoalPopup = () => {
    setConfirmationVisible(false);
    setIsUpdatingGoals(false);
    onClose?.();
  };

  const handleResetClick = () => {
    setConfirmationVisible(true);
  };

  const handleConfirmReset = () => {
    deleteUserGoals();
  };

  const handleCancelReset = () => {
    setConfirmationVisible(false);
  };

  const handleObjectiveToggle = async (objectiveId) => {
    if (!careerGoals?.goal_id || !UserID) return;
    try {
      const response = await fetch(`${baseurl}/goal/objective/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: UserID,
          goal_id: careerGoals.goal_id,
          objective_id: objectiveId,
        }),
      });
      const data = await response.json();
      if (data.status === true) {
        setCareerGoals((prev) => ({
          ...prev,
          objectives: prev.objectives.map((obj) =>
            obj.objective_id === objectiveId
              ? { ...obj, completed: !obj.completed }
              : obj
          ),
          progress: data.goal_progress_percentage ?? prev.progress,
        }));
        onSuccess?.();
      } else {
        setSnackbarMessage(data.message || "Error toggling objective");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("An error occurred while updating objective");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const careerIntentionOptions = [
    "Get a job in this field",
    "Switch career path",
    "Improve current job performance",
    "Gain industry certification",
    "Build strong project portfolio",
    "Start freelancing",
    "Explore this field before deciding",
  ];

  const targetRoleOptions = [
    "Software Developer",
    "Full Stack Developer",
    "Data Analyst",
    "AI Engineer",
    "Frontend Developer",
    "Backend Developer",
    "DevOps Engineer",
    "Product Manager",
    "UX Designer",
    "Data Scientist",
    "Other",
  ];

  const skillTypeOptions = [
    "Technical Skills",
    "Soft Skills",
    "Leadership",
    "Communication",
    "Interview Preparation",
  ];

  const renderMenu = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <div className={style.popupHead}>
              <h2>Step 1: Define Primary Career Intention</h2>
            </div>
            <div className={style.mainboxCtn}>
              <div className={style.popPara}>
                <p>What is your primary career intention? (Select all that apply)</p>
              </div>
              <div className={style.ListBox}>
                <ul>
                  {careerIntentionOptions.map((option) => (
                    <li key={option}>
                      <label>
                        <input
                          type="checkbox"
                          checked={(formData.primary_career_intention || []).includes(option)}
                          onChange={(e) =>
                            handleCheckboxChange("primary_career_intention", option, e.target.checked)
                          }
                        />
                        {option}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <div className={style.popupHead}>
              <h2>Step 2: Target Role & Level</h2>
            </div>
            <div className={style.mainboxCtn}>
              <div className={style.optionSet}>
                <p>Select your target role</p>
                <select
                  value={formData.target_role}
                  onChange={(e) => handleInputChange("target_role", e.target.value)}
                >
                  <option value="" disabled>
                    Choose your target role
                  </option>
                  {targetRoleOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className={style.optionSet}>
                <p>Select your target level</p>
                <select
                  value={formData.target_level}
                  onChange={(e) => handleInputChange("target_level", e.target.value)}
                >
                  <option value="" disabled>
                    Choose your level
                  </option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <div className={style.popupHead}>
              <h2>Step 3: Skill Focus Areas</h2>
            </div>
            <div className={style.mainboxCtn}>
              {!formData.target_role ? (
                <div className={style.popPara}>
                  <p>Please go back to Step 2 and select your target role and level first. Skills will be shown based on your selection.</p>
                </div>
              ) : skillsLoading ? (
                <div style={{ padding: "1.5rem", textAlign: "center" }}>
                  <Loader variant={theme === "dashboard" ? "dashboard" : "default"} />
                  <p style={{ marginTop: "0.5rem" }}>Loading skills for {formData.target_role}...</p>
                </div>
              ) : (
                <>
                  <div className={style.popPara}>
                    <p>Select skills to focus on for <strong>{formData.target_role}</strong>{formData.target_level ? ` (${formData.target_level})` : ""}</p>
                  </div>
                  <div className={style.ListBox}>
                    <ul>
                      {skillFocusOptions.map((option) => (
                        <li key={option}>
                          <label>
                            <input
                              type="checkbox"
                              checked={(formData.skill_focus || []).includes(option)}
                              onChange={(e) =>
                                handleCheckboxChange("skill_focus", option, e.target.checked)
                              }
                            />
                            {option}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
              <div className={style.optionSet}>
                <p>Choose skill type</p>
                <select
                  value={formData.skill_type}
                  onChange={(e) => handleInputChange("skill_type", e.target.value)}
                >
                  <option value="" disabled>
                    Choose skill type
                  </option>
                  {skillTypeOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div>
            <div className={style.popupHead}>
              <h2>Step 4: Timeline & Commitment</h2>
            </div>
            <div className={style.mainboxCtn}>
              <div className={style.optionSet}>
                <p>Select your timeline</p>
                <select
                  value={formData.timeline}
                  onChange={(e) => handleInputChange("timeline", e.target.value)}
                >
                  <option value="" disabled>
                    Choose timeline
                  </option>
                  <option value="1–3 Months">1–3 Months</option>
                  <option value="3–6 Months">3–6 Months</option>
                  <option value="6–12 Months">6–12 Months</option>
                  <option value="1+ Year">1+ Year</option>
                </select>
              </div>
              <div className={style.optionSet}>
                <p>Select your weekly commitment</p>
                <select
                  value={formData.weekly_commitment}
                  onChange={(e) => handleInputChange("weekly_commitment", e.target.value)}
                >
                  <option value="" disabled>
                    Choose weekly commitment
                  </option>
                  <option value="5–10 hours">5–10 hours</option>
                  <option value="10–20 hours">10–20 hours</option>
                  <option value="20+ hours">20+ hours</option>
                </select>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!open || !UserID) return;

    const fetchAndCheckGoals = async () => {
      setLoading(true);
      setNoLearnings(false);
      setHasGoals(false);
      setCareerGoals(null);
      setCurrentStep(1);
      setIsUpdatingGoals(false);
      setFormData(buildInitialFormData());

      try {
        const res = await fetch(`${baseurl}/userlearning/details`, {
          method: "POST",
          body: JSON.stringify({ user_id: UserID }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${key}`,
          },
        });
        const data = await res.json();

        const item = Array.isArray(data?.data) && data.data.length > 0 ? data.data[0] : null;
        const learningIds = item?.learning_id || "";
        const hasLearning = !!learningIds;

        if (hasLearning) {
          localStorage.setItem("newLearningId", learningIds);
          if (item.admin_mapped_lessons) {
            localStorage.setItem("adminmapped", item.admin_mapped_lessons);
          }
          setLearningId(learningIds);
        } else {
          // Career goals popup should still work without learnings.
          setNoLearnings(false);
          setLearningId("");
        }

        const requests = [
          hasLearning
            ? fetch(`${baseurl}/list/usergoals-check`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ user_id: UserID, learning_id: learningIds }),
            })
            : Promise.resolve(null),
          fetch(`${baseurl}/goal/get`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: UserID }),
          }),
        ];

        const [goalsRes, careerGoalsRes] = await Promise.all(requests);

        if (goalsRes) {
          const goalsData = await goalsRes.json();
          setLearningId(goalsData.learning_id || learningIds);
          setHasGoals(!!goalsData.status);
        } else {
          setHasGoals(false);
        }

        const careerData = await careerGoalsRes.json();
        if (careerData.status === true && careerData.data?.goal_id) {
          setCareerGoals(careerData.data);
          setHasGoals(true);
          setFormData(buildInitialFormData(careerData.data));
        }
      } catch (err) {
        console.error("Error fetching goals:", err);
        setNoLearnings(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAndCheckGoals();
  }, [open, UserID]);

  if (!open) return null;

  return (
    <>
      <div className={style.popupoverlay} onClick={closeGoalPopup}>
        <div
          className={style.popupcontent}
          onClick={(e) => e.stopPropagation()}
        >
          <button className={style.closeButton} onClick={closeGoalPopup}>
            &times;
          </button>

          {loading ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <Loader variant={theme === "dashboard" ? "dashboard" : "default"} />
            </div>
          ) : hasGoals && !isUpdatingGoals ? (
            <>
              <div className={style.goalboxStatic}>
                <GoalDetail />
              </div>
              {careerGoals?.goal_id && (
                <button
                  className={style.updateBtn}
                  onClick={() => setIsUpdatingGoals(true)}
                >
                  Update Goals
                </button>
              )}
              <button className={style.resetBtn} onClick={handleResetClick}>
                Reset Goals
              </button>
              {isConfirmationVisible && (
                <div className={style.confirmationOverlay}>
                  <div className={style.confirmationContent}>
                    <p>Are you sure you want to reset your goals?</p>
                    <div className={style.btnGrp}>
                      <button
                        className={style.cancelButton}
                        onClick={handleCancelReset}
                      >
                        Cancel
                      </button>
                      <button
                        className={style.confirmButton}
                        onClick={handleConfirmReset}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {isUpdatingGoals && (
                <button
                  className={style.backToViewBtn}
                  onClick={() => setIsUpdatingGoals(false)}
                >
                  ← Back to View
                </button>
              )}
              {submitting && (
                <div className={dashboardStyle.submittingOverlay}>
                  <Loader variant={theme === "dashboard" ? "dashboard" : "default"} />
                </div>
              )}
              <div className={style.goalsStepsWrapper}>
              <div className={style.progressBox}>
                  <div className={style.progressBar}>
                    {Array.from({ length: totalSteps }, (_, index) => (
                      <div
                        key={index + 1}
                        className={`${style.stepNumber} ${currentStep === index + 1 ? style.activeStep : ""}`}
                        onClick={() => setCurrentStep(index + 1)}
                      >
                        {index + 1}
                      </div>
                    ))}
                    <div
                      className={style.progress}
                      style={{
                        width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`,
                      }}
                    />
                    <div className={style.progress2} />
                  </div>
                </div>
                <div className={style.goalbox}>
                  {renderMenu()}
                </div>
                <div className={style.navigationButtons}>
                  <button onClick={prevStep} disabled={currentStep === 1}>
                    Previous
                  </button>
                  {currentStep < totalSteps ? (
                    <button onClick={nextStep}>Next</button>
                  ) : (
                    <button onClick={callUserGoalsAPI} disabled={submitting}>Finish</button>
                  )}
                </div>
              </div>
              </>
          )}
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GoalsPopup;
